import Button from 'components/Button';
import Layout from 'containers/Layout';
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import Meta from 'utils/Meta';
import RichText from 'utils/RichText';
import { sizes, colors, helpers, width, media } from 'styles/variables';
import animateScrollTo from 'animated-scroll-to';
import axios from 'axios';
import checkbox_0 from '../../static/checkbox_0.svg';
import checkbox_1 from '../../static/checkbox_1.svg';

const StyledWrapper = styled.div`
	border-top: ${sizes[0]} solid ${colors.pink.base};
	padding: ${sizes[3]} ${sizes[2]} ${sizes[4]};
	text-align: center;
	> * {
		max-width: ${width.small}px;
		margin: 0 auto;
	}
	img {
		width: 100%;
		border-radius: ${sizes[0]};
	}
	strong {
		display: inline;
		font-size: inherit;
	}
	h1 {
		font-size: 2.25rem;
		${media.large} {
			font-size: 3rem;
		}
	}
	h2 {
		font-size: 1.5rem;
		${media.large} {
			font-size: 1.5rem;
		}
	}
	em {
		color: ${colors.pink.base};
	}
	.button.--inactive {
		pointer-events: none;
		div {
			background: ${colors.background[2]};
			border-color: ${colors.background[2]};
		}
	}
	label,
	.label {
		position: relative;
		cursor: pointer;
		background: ${colors.background[0]};
		display: grid;
		grid-template-columns: auto 1fr;
		grid-gap: ${sizes[1]};
		padding: ${sizes[1]} 0;
		text-align: left;
		div:first-of-type:before {
			content: '';
			display: block;
			width: 20px;
			height: 20px;
			background: center no-repeat;
			background-size: contain;
		}
		div:first-of-type:before {
			background-image: url(${checkbox_0});
		}
		&.--active div:first-of-type:before {
			background-image: url(${checkbox_1});
		}
		strong,
		small {
			display: block;
		}
	}
	input[type='checkbox'],
	input[type='radio'] {
		display: none;
	}
`;
const Title = styled.div`
	margin-bottom: ${sizes[3]};
`;
const Item = styled.div`
	margin-bottom: ${sizes[3]};
	text-align: left;
	img,
	p,
	h1,
	h2,
	h3 {
		text-transform: none;
		margin-bottom: ${sizes[1]};
	}
	h2 {
		margin-top: ${sizes[3]};
		line-height: 140%;
	}
	p,
	li {
		font-size: 1rem;
	}
	a {
		display: inline;
		text-decoration: underline;
		color: ${colors.pink.base};
	}
	li {
		position: relative;
		padding-left: ${sizes[2]};
		&:before {
			content: '-';
			position: absolute;
			left: 0;
		}
	}
`;
const Topics = styled.div`
	display: grid;
	grid-gap: ${sizes[1]};
	padding: ${sizes[2]} 0;
	margin-bottom: ${sizes[4]};
	${media.large} {
		grid-template-columns: repeat(2, 1fr);
	}
`;
const Topic = styled.div`
	${helpers.shadow};
	padding: ${sizes[2]};
	border-radius: ${sizes[0]};
	font-weight: bold;
	border: 3px solid transparent;
	cursor: pointer;
	&.--active {
		border-color: ${colors.teal.base};
	}
`;
const NextWrapper = styled.div`
	padding: ${sizes[2]} 0;
	margin-bottom: ${sizes[4]};
`;
const Next = styled.div`
	&:first-of-type {
		${helpers.shadow};
		padding: ${sizes[2]};
		border-radius: ${sizes[0]};
		font-weight: bold;
		border: 3px solid transparent;
		line-height: 150%;
		text-transform: capitalize;
		cursor: pointer;
		width: 100%;
		max-width: 400px;
		margin: 0 auto;
		&.--active {
			border-color: ${colors.teal.base};
		}
	}
	&:last-of-type {
		padding: ${sizes[2]};
		font-size: 0.8rem;
		color: ${colors.foreground[2]};
		font-weight: normal;
		line-height: 150%;
		cursor: pointer;
		text-decoration: underline;
	}
`;
const FormWrapper = styled.div`
	display: grid;
	grid-gap: ${sizes[1]};
	padding: ${sizes[2]} 0;
	margin: 0 auto;
	margin-bottom: ${sizes[4]};
	max-width: 600px;
	input,
	textarea {
		padding: ${sizes[1]};
		border-radius: ${sizes[0]};
		border: 1px solid ${colors.background[2]};
	}
	input[readonly] {
		color: rgba(0, 0, 0, 0.5);
		background: rgba(0, 0, 0, 0.05);
		cursor: default;
	}
	textarea {
		min-width: 100%;
		max-width: 100%;
		height: 150px;
	}
	h2 {
		text-align: left;
		line-height: 125%;
	}
`;
const Error = styled.div`
	padding: ${sizes[2]};
	margin-bottom: ${sizes[2]};
	border-radius: ${sizes[0]};
	background: ${colors.red.base};
	color: ${colors.background[0]};
	display: block;
	width: 100%;
	font-size: 0.9rem;
	line-height: 150%;
`;
const Image = styled.div`
	width: 100%;
	background: #e8959a center top no-repeat;
	background-size: auto 100%;
	height: 300px;
	${media.small} {
		height: 200px;
	}
`;

export default props => {
	const [meta] = useState(require(`data/_meta`));
	let [support] = useState(require(`data/_support`));
	const [topics, setTopics] = useState([]);
	const [activeTopic, setActiveTopic] = useState(false);
	const [formStatus, setFormStatus] = useState(0);
	const [buttonText, setButtonText] = useState(false);
	const [error, setError] = useState(null);
	const [faqConfirm, setFaqConfirm] = useState(false);

	if (props.pageContext.utm_source) {
		support = JSON.parse(
			JSON.stringify(support)
				.split('utm_source=web')
				.join('utm_source=' + props.pageContext.utm_source),
		);
	}

	useEffect(() => {
		const newTopics = [];
		support.items.map((item, index) => {
			if (!newTopics.includes(item.topic)) {
				newTopics.push(item.topic);
			}
		});
		setTopics(newTopics);
	}, []);

	const handleTopicChange = topic => {
		setActiveTopic(topic);
		setFormStatus(0);
		setTimeout(() => animateScrollTo(document.querySelector('#answers'), { verticalOffset: -50 }), 1);
	};

	const handleAnotherQuestion = () => {
		animateScrollTo(document.querySelector('body'));
		setTimeout(() => {
			setFormStatus(0);
		}, 500);
	};

	const toggleContactForm = () => {
		setFormStatus(1);
		setTimeout(() => animateScrollTo(document.querySelector('#form'), { verticalOffset: -50 }), 1);
	};

	const handleConfirm = () => {
		setFaqConfirm(!faqConfirm);
		console.log(faqConfirm);
	};

	const handleForm = () => {
		const name = document.querySelector('#name').value;
		const email = document.querySelector('#email').value;
		const message = document.querySelector('#message').value;
		if (!(name.length > 0 && email.length > 0 && message.length > 0)) {
			setError('Please fill all required fields');
			return false;
		} else {
			setError(null);
			setButtonText('Sending...');
			axios
				.post('/.netlify/functions/support', {
					data: {
						env:
							process.env.NODE_ENV === 'production' &&
							!(typeof window !== 'undefined' && window.location.origin.indexOf('netlify') > -1)
								? 'prod'
								: 'dev',
						form: { name, email, message },
						topic: activeTopic,
					},
				})
				.then(() => {
					setFormStatus(2);
					setButtonText(false);
				});
		}
	};

	let itemsCount = 0;
	return (
		<>
			<Meta {...props} />
			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify({
						'@context': 'http://schema.org',
						'@type': 'WebPage',
						url: meta.domain,
						name: meta.title,
						description: meta.description,
					})}
				</script>
			</Helmet>
			<Layout>
				<Image style={{ backgroundImage: `url(${support.image.url})` }} />
				<StyledWrapper>
					<div>
						<Title>
							<RichText data={support.header} />
						</Title>
						<Topics id="topics">
							{topics.map((item, index) => (
								<Topic
									key={index}
									onClick={() => handleTopicChange(item)}
									className={activeTopic === item && '--active'}
								>
									{item}
								</Topic>
							))}
						</Topics>
						<div id="answers">
							{support.items.map((item, index) => {
								if (activeTopic === item.topic) {
									itemsCount++;
									return (
										<Item key={index}>
											<RichText data={item.body} />
										</Item>
									);
								}
							})}
						</div>
						{activeTopic && (
							<NextWrapper>
								<Next onClick={handleAnotherQuestion}>
									I have a question
									<br />
									about another topic
								</Next>
								<Next onClick={toggleContactForm} className={formStatus !== 0 && '--active'}>
									I Didn't Find Answer To My Question &<br />I Need To Contact Support
								</Next>
							</NextWrapper>
						)}
						{formStatus === 1 && (
							<FormWrapper id="form">
								<h1>Support Contact Form</h1>
								{error && <Error>{error}</Error>}
								<input id="topic" type="text" value={`Topic: ${activeTopic}`} readOnly />
								<input id="name" type="text" placeholder="Name (required)" />
								<input id="email" type="email" placeholder="Email address (required)" />
								<textarea id="message" placeholder="Your message (required)" />
								<label className={faqConfirm ? '--active' : '--inactive'}>
									<input
										type="checkbox"
										name="checkbox"
										defaultChecked={faqConfirm}
										onChange={handleConfirm}
									/>
									<div />
									<small>
										I confirm, that my question was not answered in sections above (required)
									</small>
								</label>
								<div
									onClick={handleForm}
									className={`button ${buttonText || !faqConfirm ? '--inactive' : ''}`}
								>
									<Button
										type={1}
										hue={'pink'}
										title={`${buttonText ? buttonText : 'Send Message'}`}
									/>
								</div>
							</FormWrapper>
						)}
						{formStatus === 2 && (
							<FormWrapper id="form">
								<h1>Thank you for your message!</h1>
							</FormWrapper>
						)}
					</div>
				</StyledWrapper>
			</Layout>
		</>
	);
};
